import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

/**
 * Servicio para generar PDFs de ventas
 */

/**
 * Crea un elemento HTML temporal con los datos de la venta para convertirlo a PDF
 * @param {Object} sale - Datos de la venta
 * @returns {HTMLElement} - Elemento HTML con el contenido del PDF
 */
const createSaleTemplate = (sale) => {
  console.log('Creando template con datos de venta:', sale);
  
  try {
    // Crear un contenedor temporal
    const container = document.createElement('div');
    container.style.width = '210mm'; // Tamaño A4
    container.style.padding = '10mm';
    container.style.position = 'absolute';
    container.style.left = '-9999px'; // Fuera de la pantalla
    container.style.fontFamily = 'Arial, sans-serif';
    
    // Formatear fecha
    const saleDate = sale.created_at ? new Date(sale.created_at) : new Date();
    console.log('Fecha de venta:', saleDate);
    
    const formattedDate = saleDate.toLocaleDateString('es-AR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
    
    // Verificar datos críticos
    console.log('Items de venta:', sale.items);
    const items = Array.isArray(sale.items) ? sale.items : [];
    const total = items.reduce((sum, item) => sum + (Number(item.subtotal) || 0), 0);
    console.log('Total calculado:', total);
    
    // Crear el contenido HTML
    container.innerHTML = `
      <div style="margin-bottom: 20px; text-align: center;">
        <h1 style="margin: 0; color: #2563eb; font-size: 24px;">Comprobante de Venta</h1>
        <p style="margin: 5px 0; color: #666;">Venta #${sale.display_id || sale.id || 'Sin ID'}</p>
        <p style="margin: 5px 0; color: #666;">${formattedDate}</p>
      </div>
      
      <div style="margin-bottom: 20px; display: flex; justify-content: space-between;">
        <div>
          <h3 style="margin: 0 0 5px 0; color: #333;">Datos del Cliente</h3>
          <p style="margin: 0; color: #666;">${sale.client?.name || 'Cliente no asignado'}</p>
          ${sale.client?.phone ? `<p style="margin: 0; color: #666;">Tel: ${sale.client.phone}</p>` : ''}
          ${sale.client?.email ? `<p style="margin: 0; color: #666;">Email: ${sale.client.email}</p>` : ''}
        </div>
        <div>
          <h3 style="margin: 0 0 5px 0; color: #333;">Estado</h3>
          <p style="margin: 0; color: #666;">Pago: ${sale.payment_status === 'paid' ? 'Pagado' : 'Pendiente'}</p>
          <p style="margin: 0; color: #666;">Entrega: ${sale.delivery_status === 'delivered' ? 'Entregado' : 'Pendiente'}</p>
        </div>
      </div>
      
      <table style="width: 100%; border-collapse: collapse; margin-bottom: 20px;">
        <thead>
          <tr style="background-color: #f3f4f6;">
            <th style="padding: 8px; text-align: left; border-bottom: 1px solid #ddd;">Producto</th>
            <th style="padding: 8px; text-align: center; border-bottom: 1px solid #ddd;">Cantidad</th>
            <th style="padding: 8px; text-align: right; border-bottom: 1px solid #ddd;">Precio</th>
            <th style="padding: 8px; text-align: right; border-bottom: 1px solid #ddd;">Subtotal</th>
          </tr>
        </thead>
        <tbody>
          ${items.map(item => `
            <tr>
              <td style="padding: 8px; text-align: left; border-bottom: 1px solid #ddd;">${item.title || 'Producto sin nombre'}</td>
              <td style="padding: 8px; text-align: center; border-bottom: 1px solid #ddd;">${item.quantity || 0}</td>
              <td style="padding: 8px; text-align: right; border-bottom: 1px solid #ddd;">$${(Number(item.unitPrice) || 0).toLocaleString()}</td>
              <td style="padding: 8px; text-align: right; border-bottom: 1px solid #ddd;">$${(Number(item.subtotal) || 0).toLocaleString()}</td>
            </tr>
          `).join('')}
        </tbody>
        <tfoot>
          <tr>
            <td colspan="3" style="padding: 8px; text-align: right; font-weight: bold;">Total:</td>
            <td style="padding: 8px; text-align: right; font-weight: bold;">$${total.toLocaleString()}</td>
          </tr>
        </tfoot>
      </table>
      
      <div style="margin-top: 30px; border-top: 1px solid #ddd; padding-top: 10px; text-align: center; color: #666; font-size: 12px;">
        <p>Gracias por su compra</p>
        ${sale.created_by_name ? `<p>Atendido por: ${sale.created_by_name}</p>` : ''}
      </div>
    `;
    
    console.log('Template HTML creado correctamente');
    
    // Agregar al documento para renderizar
    document.body.appendChild(container);
    
    return container;
  } catch (error) {
    console.error('Error al crear el template HTML:', error);
    throw new Error(`Error al crear el template HTML: ${error.message}`);
  }
};

/**
 * Genera un PDF para una venta
 * @param {Object} sale - Datos de la venta
 * @param {Object} options - Opciones de generación
 * @returns {Promise<Object>} - Objeto con URL y Blob del PDF generado
 */
export const generateSalePDF = async (sale, options = {}) => {
  console.log('Iniciando generación de PDF para venta:', sale?.id);
  
  if (!sale) {
    console.error('Error: No se proporcionaron datos de venta');
    throw new Error('No se proporcionaron datos de venta');
  }
  
  let container = null;
  
  try {
    // Crear el template HTML
    container = createSaleTemplate(sale);
    console.log('Template creado, convirtiendo a canvas...');
    
    // Convertir a canvas usando html2canvas
    const canvas = await html2canvas(container, {
      scale: 2, // Mayor calidad
      useCORS: true,
      logging: true // Activar logs para depuración
    });
    
    console.log('Canvas generado correctamente, creando PDF...');
    
    // Crear PDF con jsPDF
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });
    
    // Agregar la imagen del canvas al PDF
    const imgData = canvas.toDataURL('image/png');
    const imgWidth = 210; // A4 width in mm
    const imgHeight = canvas.height * imgWidth / canvas.width;
    
    pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
    console.log('Imagen agregada al PDF');
    
    // Generar el blob del PDF
    const blob = pdf.output('blob');
    console.log('Blob del PDF generado');
    
    // Crear URL para el blob
    const url = URL.createObjectURL(blob);
    console.log('URL del PDF creada:', url);
    
    return {
      url,
      blob,
      filename: `venta-${sale.display_id || sale.id || 'sin-id'}.pdf`
    };
  } catch (error) {
    console.error('Error al generar el PDF:', error);
    throw error;
  } finally {
    // Asegurarse de limpiar el DOM
    if (container && container.parentNode) {
      try {
        document.body.removeChild(container);
        console.log('Contenedor temporal eliminado del DOM');
      } catch (cleanupError) {
        console.error('Error al limpiar el contenedor temporal:', cleanupError);
      }
    }
  }
};

/**
 * Comparte un PDF usando la Web Share API
 * @param {Object} pdfData - Datos del PDF (URL, título, etc.)
 * @returns {Promise<boolean>} - True si se compartió correctamente
 */
export const sharePDF = async (pdfData) => {
  console.log('Intentando compartir PDF:', pdfData);
  
  // Verificar si el navegador soporta compartir archivos
  if (navigator.share && navigator.canShare) {
    console.log('Navegador soporta Web Share API con archivos');
    try {
      // Crear un objeto File a partir del Blob
      const file = new File([pdfData.blob], pdfData.filename, { 
        type: 'application/pdf' 
      });
      
      // Verificar si podemos compartir archivos
      const shareData = {
        title: pdfData.title || 'Comprobante de venta',
        text: pdfData.text || 'Comprobante de venta',
        files: [file]
      };
      
      console.log('Verificando si se puede compartir con archivos...');
      if (navigator.canShare(shareData)) {
        console.log('Compartiendo con archivos...');
        await navigator.share(shareData);
        console.log('PDF compartido exitosamente con archivos');
        return true;
      } else {
        console.log('El navegador no puede compartir archivos');
      }
    } catch (error) {
      console.error('Error al compartir archivo:', error);
    }
  } else {
    console.log('Navegador no soporta compartir archivos');
  }
  
  // Si no se puede compartir el archivo, intentar compartir la URL
  if (navigator.share) {
    console.log('Intentando compartir URL...');
    try {
      await navigator.share({
        title: pdfData.title || 'Comprobante de venta',
        text: pdfData.text || 'Comprobante de venta',
        url: pdfData.url
      });
      console.log('URL compartida exitosamente');
      return true;
    } catch (error) {
      console.error('Error al compartir URL:', error);
      throw error;
    }
  }
  
  console.error('Web Share API no soportada en este navegador');
  throw new Error('Web Share API no soportada en este navegador');
};

/**
 * Abre WhatsApp para compartir un mensaje con un enlace
 * @param {string} text - Texto del mensaje
 * @param {string} url - URL a compartir
 */
export const shareToWhatsApp = (text, url) => {
  console.log('Abriendo WhatsApp con texto:', text, 'y URL:', url);
  const encodedText = encodeURIComponent(`${text} ${url}`);
  window.open(`https://wa.me/?text=${encodedText}`, '_blank');
};

/**
 * Descarga un PDF
 * @param {Blob} blob - Blob del PDF
 * @param {string} filename - Nombre del archivo
 */
export const downloadPDF = (blob, filename) => {
  console.log('Descargando PDF con nombre:', filename);
  try {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || 'comprobante.pdf';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    console.log('PDF descargado correctamente');
  } catch (error) {
    console.error('Error al descargar PDF:', error);
    throw error;
  }
}; 